import c from "./TableContainer.module.scss";
import Pagination from "../utils/Pagination";
import FilterDropdown from "../utils/FilterDropdown";
import Table from "../utils/table/Table";
import { Link } from "react-router-dom";
import { Fragment, useEffect, useMemo, useState } from "react";
import { getOrganizationPlan, getOrganizations } from "../../services/organizations.service";
import debounce from "lodash.debounce";
import useLoadingHelper from "../../hooks/loading.hook";
import LoadingBox from "../loading/LoadingBox";
import EmptyBox from "../emptystate/EmptyBox";
import useDateFormatter from "../../hooks/date-formatter.hook";
import { getWorkingTemplatePlans } from "../../services/plans-template-status.service";
import FilterDropdownCollapse from "../utils/FilterDropdownCollapse";
import PlanDownloadModal from "../popups/PlanDownloadPopup";
import { formatOrganizationPlanDownloadObject } from "../../helper/formatter.helper";
import CrudApprovalPopup from "../popups/plan-compile/CrudApprovalPopup";
import LoaderPage from "../utils/LoaderPage";
import Chip from "../chip/Chip";
import {
	getAllOrganizationTypesList,
	getAllResellersList,
	getAllTagsList,
} from "../../helper/data.helper";
import { cn, getTodoTdLink } from "../../lib/utils";
import MoreOptions from "../utils/more-options/MoreOptions";
import { getRoutingPath } from "../../lib/routing";
import CounterStatus from "../plans/CounterStatus";

const DEFAULT_POPOVER_FILTERS_STATE = {
	tags: [],
	"workingOrganizationPlans.tags": [],
	"workingOrganizationPlans.organizationRequirements.statuses": [],
	"workingOrganizationPlans.approved": null,
	"workingOrganizationPlans.completed": null,
	reseller_id: [],
	"workingOrganizationPlans.template_plan_id": null,
	organization_type_id: [],
};

const DEFAULT_QUERY_PARAMS = {
	page: 1,
	filter: {
		name: "",
		...DEFAULT_POPOVER_FILTERS_STATE,
	},
	sort: "-last_activity_at,name",
};

const COMPARABLE_FILTER_KEYS = Object.keys(DEFAULT_QUERY_PARAMS.filter).filter(
	(key) => key !== "name"
);

function TableEntities({ isReseller }) {
	const role = useMemo(() => (isReseller ? "reseller" : "admin"), [isReseller]);

	const [organizations, setOrganizations] = useState([]);

	const [resellersLoading, setResellersLoading] = useState(false);

	const [resellers, setResellers] = useState([]);

	const [tagsLoading, setTagsLoading] = useState({
		organizations: false,
		plans: false,
	});

	const [tags, setTags] = useState({
		organizations: [],
		plans: [],
	});

	const [organizationTypesLoading, setOrganizationTypesLoading] = useState(false);

	const [organizationTypes, setOrganizationTypes] = useState([]);

	const [planStatus, setPlanStatus] = useState([]);

	const [planData, setPlanData] = useState(null);

	const [loadingStatus, setLoadingStatus] = useState(false);

	const [openPrinterModal, setOpenPrinterModal] = useState(false);

	const [openApprovalModal, setOpenApprovalModal] = useState(false);

	const [loadingPage, setLoadingPage] = useState(false);

	const { queueLoading, addQueue, removeQueue, initStatus, setInitStatus } = useLoadingHelper();

	const { formatDate } = useDateFormatter();

	const [queryParams, setQueryParams] = useState(DEFAULT_QUERY_PARAMS);

	const [pageData, setPageData] = useState({
		last: 0,
		current: 0,
		total: 0,
	});

	const subscriptionStatus = {
		inactive: {
			label: "Scaduto",
			classText: "text-red-100",
			classCircle: "bg-red-100",
		},
		warning: {
			label: "In scadenza",
			classText: "text-danger-100",
			classCircle: "bg-danger-100",
		},
		danger: {
			label: "In scadenza",
			classText: "text-danger-100",
			classCircle: "bg-danger-100",
		},
		active: {
			label: "Attivo",
			classText: "text-success-100",
			classCircle: "bg-success-100",
		},
	};

	/**
	 * Fetch data
	 */
	useEffect(() => {
		getListOrganizations();
	}, [queryParams]);

	/**
	 * Fetch data
	 */
	useEffect(() => {
		if (!isReseller) {
			getListResellers();
			getListTags({ scope: "organizations" });
			getListTags({ scope: "plans" });
			getListOrganizationTypes();
		}
	}, []);

	/**
	 * Get List organizations
	 */
	const getListOrganizations = () => {
		const body = {
			include: [
				"organizationType",
				"reseller",
				"workingOrganizationPlans",
				"lastActivity",
				...(!isReseller ? ["tags"] : []),
			],
			append: {
				organizationPlans: ["attachments_count", "requirements_attachments_count"],
			},
			...queryParams,
		};

		addQueue();

		// Fetch Api
		getOrganizations(
			body,
			(res) => {
				setOrganizations(res.data);

				removeQueue();

				setInitStatus(true);

				setPageData({
					last: res.meta.last_page,
					current: res.meta.current_page,
					total: res.meta.total,
				});
			},
			() => {
				removeQueue();
			}
		);
	};

	/**
	 * Get List organizations
	 */
	const getListResellers = async () => {
		setResellersLoading(true);
		const reseller = await getAllResellersList();
		setResellers(reseller);
		setResellersLoading(false);
	};

	/**
	 * Get List tags
	 */
	const getListTags = async (filters) => {
		setTagsLoading((prevState) => ({ ...prevState, [filters.scope]: true }));
		const tags = await getAllTagsList({ filter: filters });
		setTags((prevState) => ({
			organizations: filters.scope === "organizations" ? tags : prevState.organizations,
			plans: filters.scope === "plans" ? tags : prevState.plans,
		}));
		setTagsLoading((prevState) => ({ ...prevState, [filters.scope]: false }));
	};

	/**
	 * Get List organization types
	 */
	const getListOrganizationTypes = async () => {
		setOrganizationTypesLoading(true);
		const organizationTypes = await getAllOrganizationTypesList();
		setOrganizationTypes(organizationTypes);
		setOrganizationTypesLoading(false);
	};

	/**
	 * Update page
	 * @param {*} newPage
	 * @returns
	 */
	const updatePage = (newPage) => setQueryParams((q) => ({ ...q, page: newPage }));

	/**
	 * Debounce Search
	 */
	const debouncedSearch = debounce(onSearch, 300);

	/**
	 * Search event function
	 */
	function onSearch(e) {
		const searchModel = e.target.value;

		if (searchModel.trim().length > 0 || queryParams.filter.name != null) {
			setQueryParams((q) => ({
				...q,
				page: 1,
				filter: {
					...q.filter,
					name: searchModel.trim().length > 0 ? searchModel : "",
				},
			}));
		}
	}

	/**
	 * Filter by status
	 * @param {*} items
	 */
	const filterByStatus = (items) => {
		const filter = planStatus.flatMap((plan) => {
			const statusList = plan.requirement_statuses
				.filter((s) => items.includes(s.id))
				.map((s) => s.id);
			return statusList.length
				? [{ template_plan_id: plan.id, requirement_status_ids: statusList }]
				: [];
		});

		setQueryParams((q) => ({
			...q,
			page: 1,
			filter: {
				...q.filter,
				"workingOrganizationPlans.organizationRequirements.statuses": filter,
			},
		}));
	};

	/**
	 * Filter by plan
	 * @param {*} items
	 */
	const changeFilters = (key, item) =>
		setQueryParams((q) => ({
			...q,
			page: 1,
			filter: { ...q.filter, [key]: item },
		}));

	const filteredStatus = queryParams.filter[
		"workingOrganizationPlans.organizationRequirements.statuses"
	]?.length
		? queryParams.filter["workingOrganizationPlans.organizationRequirements.statuses"]
				.map((list) => list.requirement_status_ids)
				.flat(Infinity)
		: [];

	/**
	 * Reset Filter
	 */
	const resetFilter = () =>
		setQueryParams((q) => ({
			...q,
			page: 1,
			filter: {
				...q.filter,
				...DEFAULT_POPOVER_FILTERS_STATE,
			},
		}));

	const getPlanTemplates = () => {
		if (!loadingStatus) {
			setLoadingStatus(true);

			getWorkingTemplatePlans(
				{ include: ["requirementStatuses"] },
				(res) => {
					setPlanStatus(res.data);
					setLoadingStatus(false);
				},
				() => setLoadingStatus(false)
			);
		}
	};

	useEffect(() => {
		getPlanTemplates();
	}, []);

	const getPlanData = async (workingPlan) => {
		const body = {
			id: workingPlan.organization_id,
			idPlan: workingPlan.id,
			include: [
				"organization",
				"organizationChapters.organizationGoals.organizationRequirements.templateRequirement",
			],
			append: ["organization_requirements_statuses"],
		};

		const plan = await getOrganizationPlan(
			body,
			(res) => {},
			() => {}
		);

		return plan?.data ?? null;
	};

	/**
	 * Download plan
	 * @param workingPlan
	 * @returns {Promise<void>}
	 */
	const downloadPlan = async (workingPlan) => {
		setLoadingPage(true);
		const data = await getPlanData(workingPlan);

		if (data) {
			setPlanData(data);
			setTimeout(() => {
				setLoadingPage(false);
				setOpenPrinterModal(true);
			}, 100);
		}
	};

	/**
	 * Approve plan
	 * @param workingPlan
	 * @returns {Promise<void>}
	 */
	const approvePlan = async (workingPlan) => {
		setLoadingPage(true);
		const data = await getPlanData(workingPlan);

		if (data) {
			setPlanData(data);
			setTimeout(() => {
				setLoadingPage(false);
				setOpenApprovalModal(true);
			}, 100);
		}
	};

	const hasActiveFilters = useMemo(() => {
		return COMPARABLE_FILTER_KEYS.some(
			(key) => queryParams.filter[key] !== DEFAULT_QUERY_PARAMS.filter[key]
		);
	}, [queryParams.filter]);

	return (
		<>
			<div className={c.main_intro}>
				<div className={c.left_content}>
					<div className={c.main_fitlers}>
						{/* Filter Status */}
						<FilterDropdownCollapse
							options={planStatus.map((plan) => ({
								id: plan.id,
								label: plan.name,
								items: plan.requirement_statuses.map((status) => ({
									label: status.name,
									icon: `icon_16 status ${status.icon}`,
									value: status.id,
								})),
							}))}
							searchFilter={true}
							isLoading={loadingStatus}
							activeSelected={filteredStatus}
							onApply={(items) => filterByStatus(items)}
							toggleLabel="Stato linee d'azione"
						/>

						{/* Plan filter */}
						<FilterDropdown
							options={[
								{ label: "Mostra tutti", value: null },
								...planStatus.map((plan) => ({
									value: plan.id,
									label: plan.name,
								})),
							]}
							isLoading={loadingStatus}
							activeSelected={queryParams.filter["workingOrganizationPlans.template_plan_id"]}
							onApply={(item) => changeFilters("workingOrganizationPlans.template_plan_id", item)}
							toggleLabel="Piano"
							searchFilter={true}
							type="radio"
						/>

						{/* Approvals filter */}
						<FilterDropdown
							options={[
								{ label: "Mostra tutti", value: null },
								{ label: "Piani adottati", value: 1 },
								{ label: "Piani non adottati", value: 0 },
							]}
							activeSelected={queryParams.filter["workingOrganizationPlans.approved"]}
							onApply={(items) => changeFilters("workingOrganizationPlans.approved", items)}
							toggleLabel="Adozioni"
							type="radio"
						/>

						{/* Complete plans filter */}
						<FilterDropdown
							options={[
								{ label: "Mostra tutti", value: null },
								{ label: "Piani completati", value: 1 },
								{ label: "Piani non completati", value: 0 },
							]}
							activeSelected={queryParams.filter["workingOrganizationPlans.completed"]}
							onApply={(items) => changeFilters("workingOrganizationPlans.completed", items)}
							toggleLabel="Piani completati"
							type="radio"
						/>

						{!isReseller && (
							<FilterDropdown
								options={resellers.map((el) => ({
									label: el.name,
									value: el.id,
								}))}
								activeSelected={queryParams.filter.reseller_id}
								onApply={(items) => changeFilters("reseller_id", items)}
								toggleLabel="Rivenditori"
								searchFilter
								isLoading={resellersLoading}
							/>
						)}

						{!isReseller && (
							<FilterDropdown
								options={tags?.organizations?.map((el) => ({
									label: el.label,
									value: el.id,
								}))}
								activeSelected={queryParams.filter.tags}
								onApply={(items) => changeFilters("tags", items)}
								toggleLabel="Tag ente"
								searchFilter
								isLoading={tagsLoading.organizations}
							/>
						)}

						{!isReseller && (
							<FilterDropdown
								options={tags?.plans?.map((el) => ({
									label: el.label,
									value: el.id,
								}))}
								activeSelected={queryParams.filter["workingOrganizationPlans.tags"]}
								onApply={(items) => changeFilters("workingOrganizationPlans.tags", items)}
								toggleLabel="Tag piano"
								searchFilter
								isLoading={tagsLoading.plans}
							/>
						)}

						{!isReseller && (
							<FilterDropdown
								options={organizationTypes.map((el) => ({
									label: el.name,
									value: el.id,
								}))}
								activeSelected={queryParams.filter.organization_type_id}
								onApply={(items) => changeFilters("organization_type_id", items)}
								toggleLabel="Tipologia enti"
								searchFilter
								isLoading={organizationTypesLoading}
							/>
						)}

						{hasActiveFilters && (
							<button type="button" className={c.main_reset} onClick={resetFilter}>
								<div className={c.main_icon}>
									<span className="icon_16 default cross icon_mask !bg-interactive-100"></span>
								</div>
								Resetta
							</button>
						)}
					</div>
				</div>
				<div className={`${c.right_content} items-start`}>
					<div className="wrap_input_landing icon">
						<input
							type="text"
							onChange={debouncedSearch}
							className="landing"
							placeholder="Ricerca..."
						/>
						<span className="icon_16 default search"></span>
					</div>
				</div>
			</div>

			<div className="relative">
				{organizations.length > 0 ? (
					<div className="fade_in">
						<Table className="border-separate border-spacing-y-6">
							{organizations.map((el, index) => (
								<Table.Body key={el.id} className="even:bg-dark-10/20">
									<Table.Row
										className={cn(
											"action_hover_list align-top ring-1 ring-dark-10  overflow-hidden rounded-[8px]"
										)}
									>
										<Table.Cell
											width={350}
											className="shadow-[1px_0] shadow-dark-10 !p-[16px_20px_16px_16px]"
										>
											<div className="flex flex-col gap-2 sticky top-[84px]">
												<div className="">
													<div className="flex justify-between items-start">
														<div
															className={`main_cover p-2 size-9 flex items-center justify-center bg-white ring-1 ring-dark-10 rounded-[4px]`}
														>
															{el.logo_url ? (
																<img
																	src={el.logo_url}
																	className="logo size-full object-contain"
																	alt=""
																/>
															) : (
																<div className="main_icon flex">
																	<span className="icon_16 default entity"></span>
																</div>
															)}
														</div>
														<div className="spacing_items">
															<Link
																to={getRoutingPath("entity-detail", role, {
																	organizationId: el.id,
																})}
																className="link-interactive"
															>
																Dettaglio ›
															</Link>
														</div>
													</div>

													<div className="pt-4 font-semibold text-15">{el.name}</div>

													<div className="flex-1 pt-1">
														<div className={`flex items-center text-[13px] whitespace-nowrap`}>
															<span
																className={`flex items-center font-semibold ${subscriptionStatus[el?.subscription_status].classText}`}
															>
																{subscriptionStatus[el?.subscription_status].label}
															</span>
															{!!el.subscription_end_date && (
																<span className={`ml-[4px] opacity-70`}>
																	{el?.subscription_status === "active" ? "fino al" : "il"}{" "}
																	{formatDate(el.subscription_end_date, "DD MMM YYYY")}
																</span>
															)}
														</div>
													</div>
												</div>

												{!isReseller && (
													<div className="text-dark-50 text-13">
														{el?.reseller?.name ? el.reseller.name : "–"}
													</div>
												)}

												{!!el?.tags?.length && (
													<div className="flex items-center gap-3 flex-wrap pt-4 border-t border-dark-10 mt-3">
														{el?.tags?.slice(0, 4).map((tag) => (
															<Chip
																label={tag.label}
																key={tag.id}
																className="text-white font-semibold ring-0"
																style={{ backgroundColor: tag.color }}
															/>
														))}
														{el.tags.length > 4 && (
															<div
																data-tooltip-id="tooltip_small_top"
																data-tooltip-offset={10}
																data-tooltip-position-strategy="fixed"
																data-tooltip-content={el.tags
																	.slice(4)
																	.map((tag) => tag.label)
																	.join("\n")}
															>
																<Chip
																	label={`+${el.tags.length - 4}`}
																	className="text-interactive-100 font-semibold ring-0 bg-interactive-10"
																/>
															</div>
														)}
													</div>
												)}
											</div>
										</Table.Cell>

										<Table.Cell colSpan={20} className="!p-[0px_0px_0px_20px] !text-left">
											{el?.working_organization_plans?.length ? (
												<Table className="shadow-none">
													<Table.Body>
														{el?.working_organization_plans.map((plan) => (
															<Fragment key={plan.id}>
																<Table.Row className="action_hover_list !border-0">
																	<Table.Cell className="!pl-0" width={350}>
																		<Link
																			className="underline hover:opacity-70"
																			to={getRoutingPath("entity-plan-compile", role, {
																				organizationId: plan.organization_id,
																				planId: plan?.id,
																			})}
																		>
																			{plan.template_plan.name}
																		</Link>
																		<div className="text-[12px] text-dark-50 pt-2">
																			<span>
																				Adozioni: {plan?.organization_plan_approvals_count || 0}
																			</span>
																			{plan?.last_organization_plan_approval && (
																				<span className="text-[12px] whitespace-nowrap">
																					{" "}
																					- ultima il{" "}
																					{!!plan?.last_organization_plan_approval?.approved_at &&
																						formatDate(
																							plan?.last_organization_plan_approval?.approved_at,
																							"DD MMM YYYY"
																						)}
																				</span>
																			)}
																		</div>
																	</Table.Cell>

																	<Table.Cell width={300}>
																		{plan?.completion_percentage < 100 ? (
																			<div className="progress_item">
																				<div className="progress_bar">
																					<div
																						className="progress_value"
																						style={{
																							width: `${plan ? plan.completion_percentage : 0}%`,
																						}}
																					></div>
																				</div>
																				<div className="value">
																					{plan ? plan.completion_percentage : 0}%
																				</div>
																			</div>
																		) : (
																			<span className="flex items-center gap-1 text-success-100">
																				<span className="icon_16 check default icon_mask !bg-success-100"></span>
																				Preso in carico
																			</span>
																		)}
																	</Table.Cell>

																	<Table.Cell width={100}>
																		<div className="spacing_items !justify-start flex-wrap">
																			<div
																				data-tooltip-id="tooltip_small_top"
																				data-tooltip-offset={10}
																				data-tooltip-content="Allegati"
																				className="main_icon_text"
																			>
																				<span className="icon_16 default paper_clip"></span>
																				{(plan?.attachments_count ?? 0) +
																					(plan?.requirements_attachments_count ?? 0)}
																			</div>
																			<div
																				className="main_icon_text"
																				data-tooltip-id="tooltip_small_top"
																				data-tooltip-offset={10}
																				data-tooltip-content="Attività"
																			>
																				<span className="icon_16 default task"></span>
																				{plan?.todo_count || 0}
																			</div>
																		</div>
																	</Table.Cell>

																	<Table.Cell width={190}>
																		<div className="spacing_items">
																			<div className="action_list">
																				<button
																					type="button"
																					className="action"
																					data-tooltip-id="tooltip_small_top"
																					data-tooltip-offset={10}
																					data-tooltip-content="Scarica"
																					onClick={() => downloadPlan(plan)}
																				>
																					<span className="icon_16 default download icon_mask !bg-interactive-100"></span>
																				</button>

																				{!!plan?.agenda?.id && (
																					<Link
																						to={getRoutingPath("agenda-detail", role, {
																							organizationId: plan.organization_id,
																							agendaId: plan?.agenda?.id,
																						})}
																						className="action"
																						data-tooltip-id="tooltip_small_top"
																						data-tooltip-offset={10}
																						data-tooltip-content="Agenda"
																					>
																						<span className="icon_16 default book icon_mask !bg-interactive-100"></span>
																					</Link>
																				)}
																			</div>

																			<MoreOptions
																				dropdownContentProps={{
																					className: "z-[30]",
																				}}
																				options={[
																					{
																						wrapper: (
																							<Link
																								to={getRoutingPath("entity-plan-compile", role, {
																									organizationId: plan.organization_id,
																									planId: plan?.id,
																								})}
																							/>
																						),
																						label: "Compila piano",
																						icon: "icon_16 icon_mask !bg-interactive-100 default in_entity",
																					},
																					{
																						onClick: () => approvePlan(plan),
																						label: "Approva piano",
																						icon: "icon_16 icon_mask !bg-interactive-100 default approved",
																					},
																					{
																						wrapper: (
																							<Link
																								to={getRoutingPath("entity-plan-detail", role, {
																									organizationId: plan.organization_id,
																									planId: plan?.id,
																								})}
																							/>
																						),
																						label: "Dettaglio piano",
																						icon: "icon_16 icon_mask !bg-interactive-100 default piano",
																					},
																					{
																						wrapper: (
																							// biome-ignore lint/a11y/useAnchorContent: MoreOptions injects the visible label as this wrapper's children.
																							<a
																								href={getTodoTdLink({
																									templatePlanId: plan.template_plan_id,
																									organizationId:
																										role !== "organization" && plan.organization_id,
																								})}
																								target="_blank"
																								rel="noopener noreferrer"
																								aria-label="Vedi tutte le attività"
																							/>
																						),
																						label: "Vedi tutte le attività",
																						icon: "icon_16 icon_mask !bg-interactive-100 default task",
																					},
																				]}
																			/>
																		</div>
																	</Table.Cell>
																</Table.Row>
																<Table.Row
																	className={cn(
																		"action_hover_list",
																		plan?.template_plan?.tags?.length > 0 && "!border-0"
																	)}
																>
																	<Table.Cell
																		colSpan={4}
																		width="auto"
																		className="min-w-[140px] !pl-0 !pt-0"
																	>
																		<div className="spacing_items !justify-start flex-wrap">
																			{plan?.organization_requirements_statuses?.map((status) => (
																				<CounterStatus
																					tooltipLabel={status.name}
																					key={status.id}
																					value={status.count}
																					icon={cn("icon_16 status", status.icon)}
																				/>
																			))}
																		</div>
																	</Table.Cell>
																</Table.Row>
																{plan?.template_plan?.tags?.length > 0 && (
																	<Table.Row className="action_hover_list">
																		<Table.Cell
																			colSpan={4}
																			width="auto"
																			className="min-w-[140px] !pl-0 !pt-0"
																		>
																			<div className="spacing_items !justify-start flex-wrap">
																				{plan?.template_plan.tags?.map((tag) => (
																					<Chip
																						label={tag.label}
																						key={tag.id}
																						className="text-white font-semibold ring-0"
																						style={{
																							backgroundColor: tag.color,
																						}}
																					/>
																				))}
																			</div>
																		</Table.Cell>
																	</Table.Row>
																)}
															</Fragment>
														))}
													</Table.Body>
												</Table>
											) : (
												<EmptyBox
													icon="icon_30 default piano"
													title="Nessun piano attivo"
													description="Non ci sono piani attivi per questo ente"
												/>
											)}
										</Table.Cell>
									</Table.Row>
								</Table.Body>
							))}
						</Table>

						<Pagination
							last={pageData.last}
							current={pageData.current}
							total={pageData.total}
							onChange={updatePage}
						/>
					</div>
				) : (
					initStatus && (
						<EmptyBox
							icon="icon_75 default archive_entity"
							title="Nessun risultato trovato"
							description="Qui visualizzerai la lista di tutti gli enti"
						/>
					)
				)}
				{queueLoading.length > 0 && <LoadingBox blocked={!initStatus} />}
			</div>

			{planData && (
				<>
					<PlanDownloadModal
						key={planData.id}
						open={openPrinterModal}
						setOpen={setOpenPrinterModal}
						template={formatOrganizationPlanDownloadObject(planData.organization_contents)}
						idOrganization={planData.organization_id}
						idPlan={planData.id}
						statuses={planData.organization_requirements_statuses}
						templateId={planData.template_plan_id}
					/>

					{/* Approval popup */}
					<CrudApprovalPopup
						open={openApprovalModal}
						plan={planData}
						onSuccess={() => getListOrganizations()}
						setOpen={(isOpen) => setOpenApprovalModal(isOpen)}
					/>
				</>
			)}

			{loadingPage && <LoaderPage title="Caricamento in corso..." />}
		</>
	);
}

export default TableEntities;
